import {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'
import {IconMouse, IconX} from '@tabler/icons-react'
import {kinshipOf} from '../api/kin'
import {IconFocusCentered} from '@tabler/icons-react'
import {ageOf, fullName, isFemale, lifespan, nameParts} from '../api/tree'
import Portrait from '../Portrait'
import type {TreeIndex} from '../api/types'
import {
  CARD_H, CARD_W, COL_W, ROW_H, bounds, heightOf, layout, pathFor, widthOf,
  type Kin, type Layout,
} from './layout'

interface Props {
  ix: TreeIndex
  root: string
  kin: Kin
  /** кем вошедший отметил себя — его карточку выделяем отдельно */
  me: string | null
  onRoot: (h: string) => void
  onOpen: (h: string) => void
  onMenu: (h: string, x: number, y: number) => void
}

/** Подсказку про правую кнопку показываем, пока её не закроют. */
const HINT_KEY = 'ft.hint.rightclick'

type Pt = {x: number; y: number}
const ease = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)
const DUR = 900

/* Перестроение идёт ВОЛНОЙ от выбранного человека наружу, а не всем
   скопом. Когда полсотни карточек трогаются одновременно, глазу не за
   что зацепиться — видно только, что всё поехало. Волна показывает
   причину: сначала встаёт тот, кого выбрали, потом его родители и дети,
   потом дальняя родня.

   Задержка — доля от общей длительности; дальше неё волна не
   растягивается, иначе край древа приходит, когда всё уже замерло. */
const WAVE = 0.42

export default function TreeView({ix, root, kin, me, onRoot, onOpen, onMenu}: Props) {
  const canvasRef = useRef<HTMLDivElement>(null)
  const worldRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const cardRefs = useRef(new Map<string, HTMLElement>())

  // фактически нарисованные координаты — чтобы клик посреди анимации
  // подхватывал движение с текущего места, а не с плановых координат
  const live = useRef(new Map<string, Pt>())
  const liveOp = useRef(new Map<string, number>())
  const prev = useRef<Layout | null>(null)
  const anim = useRef(0)

  const view = useRef({k: 1, panX: 0, panY: 0})
  const [mounted, setMounted] = useState<string[]>([])
  /* Отклик на новом корне держим только на время анимации: постоянная
     пульсация в интерфейсе, куда смотрят подолгу, быстро надоедает. */
  const [pulse, setPulse] = useState(false)
  /* Трогал ли человек вид, пока камера ехала. Если трогал — камеру
     отпускаем: спорить с рукой пользователя интерфейс не должен. */
  const grabbed = useRef(false)
  /* Для какого корня камеру уже подвели. Анимация перезапускается и
     сама по себе — после неё пересобирается список смонтированных
     карточек, — и без этой отметки камера уезжала к корню каждый раз,
     то есть постоянно. По древу от этого нельзя было двигаться: любой
     сдвиг откатывался назад. */
  const [hint, setHint] = useState(() => !localStorage.getItem(HINT_KEY))

  const target = useMemo(() => layout(ix, root, kin), [ix, root, kin])
  /** Свежая раскладка для цикла камеры: он зависит только от корня,
   *  а положение узла берёт отсюда — иначе перечитывание данных
   *  дёргало бы вид. */
  const latest = useRef(target)
  latest.current = target

  /* Кем каждый приходится корню. Считается по всей выборке разом:
     на 70 персонах это доли миллисекунды, а в карточке видно сразу,
     кто перед тобой — двоюродный дед или муж двоюродной сестры. */
  /* Слово и расшифровка: «Золовка» на карточке, «сестра мужа» —
     подсказкой. Термины свойства путают даже тех, кто их знает. */
  const kins = useMemo(() => {
    const out = new Map<string, {term: string; plain: string}>()
    for (const id of target.pos.keys()) {
      out.set(id, kinshipOf(ix, root, id))
    }
    return out
  }, [ix, root, target])

  const applyView = useCallback(() => {
    const {k, panX, panY} = view.current
    if (worldRef.current) worldRef.current.style.transform = `translate(${panX}px,${panY}px) scale(${k})`
  }, [])

  const fit = useCallback((l: Layout) => {
    const c = canvasRef.current
    if (!c) return
    const {x0, x1, y0, y1} = bounds(l.pos)
    const vw = c.clientWidth - 80
    const vh = c.clientHeight - 150
    if (vw <= 0 || vh <= 0 || !isFinite(x0)) return
    view.current.k = Math.max(0.18, Math.min(1.05, vw / (x1 - x0), vh / (y1 - y0)))
    view.current.panX = (-(x0 + x1) / 2) * view.current.k
    view.current.panY = (-(y0 + y1) / 2) * view.current.k
    applyView()
  }, [applyView])

  /** Вид при первом показе. Всё древо целиком — это 70 карточек в ряд и
   *  масштаб 0.28: лиц не разобрать, а ради них карточку и увеличивали.
   *  Поэтому когда древо в экран не влезает, встаём поближе к своему
   *  корню; «вписать» по кнопке показывает картину целиком, как и раньше. */
  const frame = useCallback((l: Layout, focus: string) => {
    const c = canvasRef.current
    if (!c) return
    const {x0, x1, y0, y1} = bounds(l.pos)
    const vw = c.clientWidth - 80
    const vh = c.clientHeight - 150
    if (vw <= 0 || vh <= 0 || !isFinite(x0)) return
    const whole = Math.min(vw / (x1 - x0), vh / (y1 - y0))
    const n = l.pos.get(focus)
    if (whole >= 0.45 || !n) { fit(l); return }
    view.current.k = 0.62
    view.current.panX = -n.x * 0.62
    view.current.panY = -n.y * 0.62
    applyView()
  }, [applyView, fit])

  /** Один кадр: пишем transform каждой карточке и перерисовываем связи. */
  const draw = useCallback((cur: Record<string, Pt>, op: Record<string, number>, links: {d: string; o: number; hl: boolean; tone: number}[]) => {
    cardRefs.current.forEach((el, id) => {
      const p = cur[id]
      if (!p) {
        el.style.display = 'none'
        return
      }
      el.style.display = ''
      const o = op[id] ?? 1
      el.style.transform =
        `translate(${p.x - widthOf(id) / 2}px,${p.y - heightOf(id) / 2}px) scale(${0.94 + 0.06 * o})`
      el.style.opacity = String(o)
    })
    if (svgRef.current) {
      svgRef.current.innerHTML = links
        .map(l => {
          const cls = l.hl ? 'hl' : l.tone ? 'alt' : ''
          return `<path d="${l.d}" opacity="${l.o}"${cls ? ` class="${cls}"` : ''}/>`
        })
        .join('')
    }
  }, [])

  // монтируем объединение старого и нового набора — уходящие узлы
  // должны дожить до конца анимации
  useLayoutEffect(() => {
    const union = new Set<string>([...cardRefs.current.keys(), ...target.pos.keys()])
    setMounted([...union])
  }, [target])

  /* Камера доводится к новому корню — своим циклом, а не вместе с
   * карточками.
   *
   * Раскладка ставит корень в x = 0, но вид оставался там, где его
   * оставили: древо перестраивалось, а человек, вокруг которого всё
   * построено, уезжал за край экрана. Держать проезд внутри анимации
   * карточек нельзя: она перезапускается сама (после неё пересобирается
   * список смонтированных), и второй запуск отменял первый — камера,
   * тронувшись, замирала на полпути.
   *
   * Зависимость только от корня: перечитывание данных вид не трогает.
   * Масштаб тоже не трогаем — приближение это настройка зрителя. */
  useEffect(() => {
    if (!prev.current) return
    const n = latest.current.pos.get(root)
    if (!n) return
    const k = view.current.k
    const a = {x: view.current.panX, y: view.current.panY}
    const b = {x: -n.x * k, y: -n.y * k}
    grabbed.current = false
    const t0 = performance.now()
    let raf = 0
    const step = (now: number) => {
      // взялись тянуть полотно — камера умолкает до следующего перестроения
      if (grabbed.current) return
      const t = Math.min(1, (now - t0) / DUR)
      const e = ease(t)
      view.current.panX = a.x + (b.x - a.x) * e
      view.current.panY = a.y + (b.y - a.y) * e
      applyView()
      if (t < 1) raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [root, applyView])

  /* Отклик зажигаем при смене корня и гасим по концу анимации: держать
     его дольше незачем, а совсем без него источник волны не найти. */
  useEffect(() => {
    if (!prev.current) return
    setPulse(true)
    const t = setTimeout(() => setPulse(false), DUR)
    return () => clearTimeout(t)
  }, [root])

  useEffect(() => {
    const from = prev.current
    const ids = new Set<string>([...cardRefs.current.keys(), ...target.pos.keys()])
    // ждём, пока React смонтирует новые карточки
    if ([...target.pos.keys()].some(id => !cardRefs.current.has(id))) return

    const at = (id: string): Pt | undefined => live.current.get(id) || from?.pos.get(id)
    const focusOld = at(root) || (from ? at([...from.pos.keys()][0]) : undefined) || {x: 0, y: 0}
    const tRoot = target.pos.get(root)!
    const focusNew = {x: tRoot.x, y: tRoot.y}

    const A: Record<string, Pt> = {}
    const B: Record<string, Pt> = {}
    const o0: Record<string, number> = {}
    const o1: Record<string, number> = {}

    ids.forEach(id => {
      const a = at(id)
      const b = target.pos.get(id)
      const startOp = liveOp.current.get(id) ?? (a ? 1 : 0)
      if (a && b) { A[id] = a; B[id] = b; o0[id] = startOp; o1[id] = 1 }
      else if (b) { A[id] = focusOld; B[id] = b; o0[id] = startOp; o1[id] = 1 }
      else { A[id] = a || focusOld; B[id] = focusNew; o0[id] = startOp; o1[id] = 0 }
    })

    const key = (l: {kind: string; a: string; b: string | null; c?: string}) => `${l.kind}:${l.a}:${l.b}:${l.c}`
    const oldMap = new Map((from?.links || []).map(l => [key(l), l]))
    const plan = target.links.map(l => ({l, from: oldMap.has(key(l)) ? 1 : 0, to: 1}))
    const newKeys = new Set(target.links.map(key))
    oldMap.forEach((l, k) => { if (!newKeys.has(k)) plan.push({l, from: 1, to: 0}) })

    /* Расстояние до нового корня: по вертикали в поколениях, по
       горизонтали в ширинах колонки — иначе высокое древо всегда
       «дальше» широкого просто из-за разного масштаба осей. */
    const dist: Record<string, number> = {}
    let maxDist = 0
    ids.forEach(id => {
      const b = B[id]
      const d = Math.hypot((b.x - focusNew.x) / COL_W, (b.y - focusNew.y) / ROW_H)
      // уходящие не ждут: их убираем первыми, чтобы не мешали смотреть
      dist[id] = o1[id] === 0 ? 0 : d
      if (dist[id] > maxDist) maxDist = dist[id]
    })

    const first = !from
    const t0 = performance.now()
    cancelAnimationFrame(anim.current)

    const step = (now: number) => {
      const t = first ? 1 : Math.min(1, (now - t0) / DUR)
      const e = ease(t)
      const cur: Record<string, Pt> = {}
      const op: Record<string, number> = {}
      ids.forEach(id => {
        const d = maxDist > 0 ? WAVE * Math.min(1, dist[id] / maxDist) : 0
        const el = first ? 1 : ease(t <= d ? 0 : Math.min(1, (t - d) / (1 - d)))
        cur[id] = {x: A[id].x + (B[id].x - A[id].x) * el, y: A[id].y + (B[id].y - A[id].y) * el}
        op[id] = o0[id] + (o1[id] - o0[id]) * el
        live.current.set(id, cur[id])
        liveOp.current.set(id, op[id])
      })
      draw(
        cur, op,
        plan
          .map(p => ({
            d: pathFor(p.l, cur) || '',
            o: p.from + (p.to - p.from) * (p.to === 0 ? Math.min(1, e * 1.8) : e),
            hl: p.l.c === root || p.l.a === root,
            tone: p.l.tone || 0,
          }))
          .filter(p => p.d),
      )
      if (t < 1) anim.current = requestAnimationFrame(step)
      else {
        for (const id of [...cardRefs.current.keys()]) {
          if (!target.pos.has(id)) { live.current.delete(id); liveOp.current.delete(id) }
        }
        prev.current = target
        const alive = [...target.pos.keys()]
        // сравниваем по составу: новый массив тех же людей перезапускал
        // эффект вхолостую, а с ним и всю анимацию
        setMounted(m => (m.length === alive.length && alive.every(id => m.includes(id)) ? m : alive))
      }
    }
    anim.current = requestAnimationFrame(step)
    if (first) { prev.current = target; frame(target, root) }
    return () => cancelAnimationFrame(anim.current)
  }, [target, mounted, root, draw, frame])

  /* ---------- панорама и масштаб ---------- */
  useEffect(() => {
    const c = canvasRef.current
    if (!c) return
    const zoomAt = (cx: number, cy: number, f: number) => {
      const v = view.current
      const nk = Math.max(0.18, Math.min(2.2, v.k * f))
      v.panX = cx - (cx - v.panX) * (nk / v.k)
      v.panY = cy - (cy - v.panY) * (nk / v.k)
      v.k = nk
      applyView()
    }
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      grabbed.current = true
      const r = c.getBoundingClientRect()
      zoomAt(e.clientX - r.left - r.width / 2, e.clientY - r.top - r.height / 2, e.deltaY < 0 ? 1.12 : 1 / 1.12)
    }
    let drag = false, lx = 0, ly = 0
    const down = (e: PointerEvent) => {
      /* Полотно перехватывает указатель на себя (setPointerCapture), и всё,
         что дальше, приходит ему, а не кнопке. Поэтому щелчки по всему, что
         лежит поверх полотна, до кнопок не доезжали — подсказка не
         закрывалась. Пропускаем такие события мимо перетаскивания. */
      if ((e.target as HTMLElement).closest('.card,.treehint,.zoombox')) return
      grabbed.current = true
      drag = true; lx = e.clientX; ly = e.clientY
      c.classList.add('drag'); c.setPointerCapture(e.pointerId)
    }
    const move = (e: PointerEvent) => {
      if (!drag) return
      view.current.panX += e.clientX - lx
      view.current.panY += e.clientY - ly
      lx = e.clientX; ly = e.clientY
      applyView()
    }
    const up = () => { drag = false; c.classList.remove('drag') }
    c.addEventListener('wheel', onWheel, {passive: false})
    c.addEventListener('pointerdown', down)
    c.addEventListener('pointermove', move)
    c.addEventListener('pointerup', up)
    ;(c as unknown as {__zoom: typeof zoomAt}).__zoom = zoomAt
    return () => {
      c.removeEventListener('wheel', onWheel)
      c.removeEventListener('pointerdown', down)
      c.removeEventListener('pointermove', move)
      c.removeEventListener('pointerup', up)
    }
  }, [applyView])

  const zoom = (f: number) => {
    const c = canvasRef.current as unknown as {__zoom?: (x: number, y: number, f: number) => void} | null
    c?.__zoom?.(0, 0, f)
  }

  const setRef = (id: string) => (el: HTMLElement | null) => {
    if (el) cardRefs.current.set(id, el)
    else cardRefs.current.delete(id)
  }

  return (
    <div className="canvas" ref={canvasRef}>
      <div className="world" ref={worldRef}>
        <svg className="links" ref={svgRef} viewBox="-6000 -4000 12000 8000" />

        {/* Подпись к полосе ещё не связанных: без неё ряд карточек под
            древом читается как оборванная ветка. */}
        {target.loose.size > 0 && (() => {
          const at = [...target.loose].map(h => target.pos.get(h)!).filter(Boolean)
          const x = Math.min(...at.map(p => p.x))
          const y = at[0].y
          return (
            <div className="loosecap"
                 style={{transform: `translate(${x - CARD_W / 2}px, ${y - CARD_H / 2 - 34}px)`}}>
              Пока не связаны с древом — правая кнопка добавит им родню
            </div>
          )
        })()}
        {mounted.map(id => {
          const p = ix.people.get(id)
          const life = lifespan(p)
          const age = ageOf(ix, p)
          const nm = nameParts(p)
          return (
            <div
              key={id}
              ref={setRef(id)}
              className={
                'card' + (isFemale(p) ? ' f' : '') +
                (id === root ? ' root' : '') + (id === root && pulse ? ' pulse' : '') +
                (id === me ? ' me' : '') + (target.loose.has(id) ? ' loose' : '')
              }
              style={{width: CARD_W, height: CARD_H}}
              /* Щелчок открывает подробности сбоку. Раньше он перестраивал
                 древо вокруг человека — самое частое действие оказывалось
                 самым разрушительным: посмотреть, кто это, было нельзя без
                 того, чтобы потерять текущий вид. Перестроение переехало на
                 стрелку. */
              onClick={() => onOpen(id)}
              onContextMenu={e => { e.preventDefault(); onMenu(id, e.clientX, e.clientY) }}
              title={fullName(p)}
            >
              <Portrait p={p} size={116} shape="rounded" />
              <div className="nm">
                <span className="nsur">
                  {nm.main}
                  {nm.alt && <em className="nmaid">({nm.alt})</em>}
                </span>
                <span className="ngiv">{nm.given}</span>
              </div>
              <div className="dt">{life || ' '}{age && <em className="age">{age}</em>}</div>
              <div className="kin" title={kins.get(id)?.plain || undefined}>
                {kins.get(id)?.term || ' '}
              </div>
              <button
                className="open"
                title="Построить древо вокруг этого человека"
                onClick={e => { e.stopPropagation(); onRoot(id) }}
              >
                <IconFocusCentered size={15} stroke={1.8} />
              </button>
            </div>
          )
        })}
      </div>

      {/* Правый клик по карточке — единственный способ завести родню,
          и сам по себе он не угадывается. Подсказку можно закрыть, и
          больше она не вернётся: помним отказ в браузере. */}
      {hint && (
        <div className="treehint">
          <IconMouse size={16} stroke={1.7} />
          <span>
            Щелчок по карточке открывает подробности сбоку, значок в углу
            строит древо вокруг этого человека. <b>Правая кнопка</b> — добавить
            родителей, детей, супруга, братьев и сестёр.
          </span>
          <button type="button" onClick={() => {
            localStorage.setItem(HINT_KEY, '1')
            setHint(false)
          }}>
            <IconX size={15} stroke={1.9} />
          </button>
        </div>
      )}

      <div className="zoombox">
        <button onClick={() => zoom(1.2)} title="Приблизить">+</button>
        <button onClick={() => zoom(1 / 1.2)} title="Отдалить">−</button>
        <button onClick={() => fit(target)} title="Вписать">⤢</button>
      </div>
    </div>
  )
}
